import React, { useEffect, useMemo, useState } from "react";
import { Box, TextField, InputAdornment, TablePagination, TableSortLabel, Button } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import { endPoints } from "../../constant/Environment";
import { getAllData, addData } from "../../Utility/API";
import Toaster from "../../components/Toast";
import { ErrorToast, SuccesToast } from "../../components/Toast/message";
import Loader from "../../components/Loader";
import PageHeader from "../../components/PageHeader";
import SectionCard from "../../components/SectionCard";
import { tokens } from "../../theme";
import { mono, pill, th, td, TablePaginationActions } from "../../components/tableKit";

export default function States() {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "state_name", direction: "asc" });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    fetchStates();
  }, []);

  const fetchStates = async () => {
    setIsLoading(true);
    try {
      const response = await getAllData(endPoints.api.GET_STATES);
      if (response?.status === "success") {
        setRows(response.data || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleBlock = async (row) => {
    const nextBlocked = !row.blocked;
    setUpdatingId(row.state_id);
    try {
      const response = await addData(`${endPoints.api.GET_STATES}/${row.state_id}/block?blocked=${nextBlocked}`);
      if (response?.data?.status === "success") {
        SuccesToast(response.data.message || `State ${nextBlocked ? "blocked" : "unblocked"}`);
        setRows((prev) => prev.map((r) => (r.state_id === row.state_id ? { ...r, blocked: nextBlocked } : r)));
      } else {
        ErrorToast(response?.data?.message || "Could not update the state");
      }
    } catch (e) {
      console.error(e);
      ErrorToast("Could not update the state");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
    setPage(0);
  };

  const rowsToDisplay = useMemo(() => {
    let result = rows;

    if (searchText) {
      const q = searchText.toLowerCase();
      result = result.filter(
        (r) =>
          String(r.state_name || "").toLowerCase().includes(q) ||
          String(r.state_code || "").toLowerCase().includes(q) ||
          String(r.state_id || "").includes(q) ||
          String(r.country_id || "").includes(q)
      );
    }

    if (sortConfig.key) {
      result = [...result].sort((a, b) => {
        let av = a[sortConfig.key] ?? "";
        let bv = b[sortConfig.key] ?? "";
        let cmp;
        if (!isNaN(Number(av)) && !isNaN(Number(bv)) && av !== "" && bv !== "") {
          cmp = Number(av) - Number(bv);
        } else {
          cmp = String(av).localeCompare(String(bv), undefined, { sensitivity: "base" });
        }
        return sortConfig.direction === "asc" ? cmp : -cmp;
      });
    }

    return result;
  }, [rows, searchText, sortConfig]);

  const sortableTh = (key, label) => (
    <th style={th}>
      <TableSortLabel
        active={sortConfig.key === key}
        direction={sortConfig.key === key ? sortConfig.direction : "asc"}
        onClick={() => handleSort(key)}
        sx={{ "&.MuiTableSortLabel-root": { color: "inherit" }, "& .MuiTableSortLabel-icon": { fontSize: 14 } }}
      >
        {label}
      </TableSortLabel>
    </th>
  );

  return (
    <Box>
      <Toaster></Toaster>
      {isLoading && <Loader spinner={true} visible={isLoading} />}

      <PageHeader title="States" crumb="Operate / States" subtitle="States available for player address & compliance checks" />

      <SectionCard>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", mb: 2 }}>
          <TextField
            size="small"
            placeholder="Search by state name, code, or ID"
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 320, "& .MuiInputBase-root": { fontSize: 13 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={{ overflowX: "auto" }}>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {sortableTh("state_id", "State ID")}
                {sortableTh("country_id", "Country ID")}
                {sortableTh("state_name", "State Name")}
                {sortableTh("state_code", "State Code")}
                <th style={th}>Status</th>
                <th style={th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rowsToDisplay.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={6} style={{ ...td, textAlign: "center", color: tokens.textDim }}>
                    No states found
                  </td>
                </tr>
              )}
              {rowsToDisplay
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <tr key={row.state_id}>
                    <td style={{ ...td, fontFamily: mono }}>{row.state_id}</td>
                    <td style={{ ...td, fontFamily: mono }}>{row.country_id}</td>
                    <td style={td}>{row.state_name}</td>
                    <td style={{ ...td, fontFamily: mono }}>{row.state_code}</td>
                    <td style={td}>{pill(row.blocked ? "Blocked" : "Active", row.blocked ? "bad" : "ok")}</td>
                    <td style={td}>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={updatingId === row.state_id}
                        onClick={() => handleToggleBlock(row)}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: 12,
                          color: row.blocked ? tokens.secondary : tokens.danger,
                          borderColor: row.blocked ? `${tokens.secondary}66` : `${tokens.danger}66`,
                        }}
                      >
                        {row.blocked ? "Unblock" : "Block"}
                      </Button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Box>
        </Box>

        <TablePagination
          component="div"
          rowsPerPageOptions={[10, 25, 50, 100]}
          count={rowsToDisplay.length}
          rowsPerPage={rowsPerPage}
          page={page}
          SelectProps={{ inputProps: { "aria-label": "rows per page" }, native: true }}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          ActionsComponent={TablePaginationActions}
          sx={{ borderTop: `1px solid ${tokens.border}`, mt: 1 }}
        />
      </SectionCard>
    </Box>
  );
}
